import React from 'react';
import {
  Chip,
  Tooltip,
  Box,
  Typography,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Cancel as BlockedIcon,
  Schedule as WaitingIcon,
  HelpOutline as UnknownIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { parseISO } from 'date-fns';

/**
 * Display dispatch status indicator for an observation
 * Shows if observation can be dispatched now and why it's blocked
 */
const DispatchStatusIndicator = ({ observationId, status, dispatchInfo, compactMode = false }) => {
  // Only show for pending and waiting_next observations (observations that need to run)
  if (status !== 'pending' && status !== 'waiting_next') {
    return null;
  }

  // Don't show if no dispatch info available
  if (!dispatchInfo) {
    return null;
  }

  const getStatusIcon = () => {
    switch (dispatchInfo.dispatch_status) {
      case 'ready':
        return <CheckIcon fontSize="small" />;
      case 'blocked':
        return <BlockedIcon fontSize="small" />;
      case 'waiting':
        return <WaitingIcon fontSize="small" />;
      default:
        return <UnknownIcon fontSize="small" />;
    }
  };

  const getStatusColor = () => {
    switch (dispatchInfo.dispatch_status) {
      case 'ready':
        return 'success';
      case 'blocked':
        return 'error';
      case 'waiting':
        return 'warning';
      default:
        return 'default';
    }
  };

  const getStatusLabel = () => {
    switch (dispatchInfo.dispatch_status) {
      case 'ready':
        return 'Ready';
      case 'blocked':
        return 'Blocked';
      case 'waiting':
        return 'Waiting';
      default:
        return 'Unknown';
    }
  };

  const formatTimeUTC = (timeString) => {
    if (!timeString) return null;
    try {
      const date = parseISO(timeString);
      // Format as: MM/DD HH:mm:ss UTC
      const month = (date.getUTCMonth() + 1).toString().padStart(2, '0');
      const day = date.getUTCDate().toString().padStart(2, '0');
      const hours = date.getUTCHours().toString().padStart(2, '0');
      const minutes = date.getUTCMinutes().toString().padStart(2, '0');
      const seconds = date.getUTCSeconds().toString().padStart(2, '0');
      return `${month}/${day} ${hours}:${minutes}:${seconds} UTC`;
    } catch (e) {
      return null;
    }
  };

  const renderTooltipContent = () => {
    const nextObservableTime = formatTimeUTC(dispatchInfo.next_observable_time);

    if (dispatchInfo.dispatch_status === 'ready') {
      return (
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
            Ready to dispatch
          </Typography>
          <Typography variant="caption">
            All constraints are passing. This observation can be scheduled.
          </Typography>
          {nextObservableTime && (
            <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontStyle: 'italic' }}>
              Next run: {nextObservableTime}
            </Typography>
          )}
        </Box>
      );
    }

    if (dispatchInfo.dispatch_status === 'blocked' || dispatchInfo.dispatch_status === 'waiting') {
      const failedConstraints = dispatchInfo.constraint_checks?.filter(c => c.status === 'failing') || [];

      return (
        <Box sx={{ maxWidth: 400 }}>
          <Typography variant="body2" sx={{ fontWeight: 'bold', mb: 1 }}>
            {dispatchInfo.dispatch_status === 'blocked' ? 'Blocked - Cannot dispatch now' : 'Waiting for constraints'}
          </Typography>

          {nextObservableTime && (
            <Typography variant="caption" sx={{ display: 'block', mb: 1, fontStyle: 'italic', color: 'info.main' }}>
              Next observable: {nextObservableTime}
            </Typography>
          )}

          {failedConstraints.length > 0 && (
            <>
              <Typography variant="caption" sx={{ mb: 1, display: 'block' }}>
                Failing constraints:
              </Typography>
              <List dense disablePadding>
                {failedConstraints.map((check, idx) => (
                  <ListItem key={idx} disablePadding sx={{ mb: 0.5 }}>
                    <ListItemIcon sx={{ minWidth: 24 }}>
                      {check.blocking ? <BlockedIcon fontSize="small" color="error" /> : <WarningIcon fontSize="small" color="warning" />}
                    </ListItemIcon>
                    <ListItemText
                      primary={check.message}
                      primaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}

          {dispatchInfo.actions && dispatchInfo.actions.length > 0 && (
            <>
              <Typography variant="caption" sx={{ mt: 1, mb: 0.5, display: 'block', fontWeight: 'bold' }}>
                Suggested actions:
              </Typography>
              <List dense disablePadding>
                {dispatchInfo.actions.map((action, idx) => (
                  <ListItem key={idx} disablePadding>
                    <ListItemText
                      primary={action.message}
                      primaryTypographyProps={{ variant: 'caption' }}
                    />
                  </ListItem>
                ))}
              </List>
            </>
          )}
        </Box>
      );
    }

    return (
      <Typography variant="caption">
        Dispatch status unknown - constraint check failed
      </Typography>
    );
  };

  if (compactMode) {
    return (
      <Tooltip title={renderTooltipContent()} arrow>
        <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', ml: 0.5 }}>
          {getStatusIcon()}
        </Box>
      </Tooltip>
    );
  }

  return (
    <Tooltip title={renderTooltipContent()} arrow>
      <Chip
        icon={getStatusIcon()}
        label={getStatusLabel()}
        color={getStatusColor()}
        size="small"
        variant="outlined"
        sx={{ ml: 0.5 }}
      />
    </Tooltip>
  );
};

export default DispatchStatusIndicator;
